import React, { useState, useEffect, useRef, useContext } from "react";
import "./index.css";
import TierList from "./components/TierList";
import ImageHolder from "./components/ImageHolder";
import { AddTierButton } from "./components/TierModal";
import { clearAllImageStores, migrateImageStoresFromLocalStorage } from "./utils/imageStore";
import { exportTiersToJson, parseImportFile, restoreImportData } from "./utils/exportImport";

const QuestionIcon: React.FC = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="12" cy="12" r="10" />
		<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
		<line x1="12" y1="17" x2="12.01" y2="17" />
	</svg>
);

const StorageIcon: React.FC = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
		<ellipse cx="12" cy="5" rx="9" ry="3" />
		<path d="M3 5v4c0 1.657 4.03 3 9 3s9-1.343 9-3V5" />
		<path d="M3 9v4c0 1.657 4.03 3 9 3s9-1.343 9-3V9" />
		<path d="M3 13v4c0 1.657 4.03 3 9 3s9-1.343 9-3v-4" />
	</svg>
);

const HowToEditModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
	const handleBgClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if ((e.target as HTMLElement).id === "help-modal-bg") onClose();
	};

	return (
		<div
			id="help-modal-bg"
			className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
			onMouseDown={handleBgClick}
		>
			<div className="bg-zinc-800 p-6 rounded-md shadow-md max-w-sm w-full text-white">
				<h2 className="text-lg font-semibold mb-4">How To Edit</h2>
				<ul className="space-y-3 text-gray-300 text-sm">
					<li>
						<span className="font-semibold text-white">Edit Tier Color:</span> Right-click on any tier label to open the context menu. Use the color picker to change the tier's background color.
					</li>
					<li>
						<span className="font-semibold text-white">Edit Tier Name:</span> Right-click on any tier label to open the context menu. Type in the "Edit Name" field to update the tier's label text.
					</li>
					<li>
						<span className="font-semibold text-white">Reorder Tiers:</span> Click and drag a tier label to reorder the tiers.
					</li>
					<li>
						<span className="font-semibold text-white">Delete a Tier:</span> Right-click a tier label and click "Delete Tier" at the bottom of the context menu.
					</li>
					<li>
						<span className="font-semibold text-white">Edit An Item's Label:</span> Double-click on an item to open the label editor. Type in the "Edit Label" field to update the item's label text.
					</li>
				</ul>
				<div className="flex justify-end mt-6">
					<button
						onClick={onClose}
						className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
					>
						Got it
					</button>
				</div>
			</div>
		</div>
	);
};

interface Style {
	ratio: string;
	size: number;
	quality: number;
	pasteScaleMode: "fixed" | "preserve";
}

interface StyleState {
	style: Style;
	setStyle: React.Dispatch<React.SetStateAction<Style>>;
}

interface Tier {
	id: number;
	color: string;
	tierLabel: string;
}

interface TierState {
	tiers: Tier[];
	setTiers: React.Dispatch<React.SetStateAction<Tier[]>>;
}

export const StylingContext = React.createContext<StyleState>({} as StyleState);
export const TierContext = React.createContext<TierState>({} as TierState);

const GlobalSettingsMenu: React.FC = () => {
	const { tiers } = useContext(TierContext);
	const { style } = useContext(StylingContext);
	const [isOpen, setIsOpen] = useState(false);
	const importInputRef = useRef<HTMLInputElement>(null);

	const handleClearLocalStorage = async () => {
		setIsOpen(false);
		localStorage.clear();
		await clearAllImageStores();
		window.location.reload();
	};

	const handleExport = async () => {
		setIsOpen(false);
		await exportTiersToJson(tiers, style);
	};

	const handleImportChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		try {
			const data = await parseImportFile(file);
			localStorage.setItem("tiers", JSON.stringify(data.tiers));
			if (data.style) localStorage.setItem("style", JSON.stringify(data.style));
			await restoreImportData(data);
			setIsOpen(false);
			window.location.reload();
		} catch (error) {
			console.error("Failed to import:", error);
			window.alert("Failed to import file: " + (error as Error).message);
		}

		if (importInputRef.current) importInputRef.current.value = "";
	};

	const handleBgClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if ((e.target as HTMLElement).id === "storage-modal-bg") setIsOpen(false);
	};

	return (
		<div className="sm:ml-auto">
			<button
				onClick={() => setIsOpen(true)}
				className="flex items-center justify-center w-9 h-9 bg-stone-600 hover:bg-stone-500 text-gray-300 hover:text-white rounded"
				title="Storage"
				aria-label="Storage"
			>
				<StorageIcon />
			</button>
			{isOpen && (
				<div
					id="storage-modal-bg"
					className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
					onMouseDown={handleBgClick}
				>
					<div className="bg-zinc-800 p-6 rounded-md shadow-md w-72">
						<h2 className="text-lg font-semibold mb-4 text-white">Storage</h2>
						<div className="flex flex-col gap-2">
							<button
								onClick={handleExport}
								className="w-full px-4 py-2 bg-stone-600 hover:bg-stone-500 text-white text-sm font-medium rounded"
							>
								Save Tier List
							</button>
							<button
								onClick={() => importInputRef.current?.click()}
								className="w-full px-4 py-2 bg-stone-600 hover:bg-stone-500 text-white text-sm font-medium rounded"
							>
								Load Tier List
							</button>
						</div>
						<div className="border-t border-zinc-700 my-4" />
						<button
							onClick={handleClearLocalStorage}
							className="w-full px-4 py-2 bg-red-700 hover:bg-red-600 text-white text-sm font-medium rounded"
						>
							Reset Tier List Board
						</button>
						<div className="flex justify-end mt-4">
							<button
								onClick={() => setIsOpen(false)}
								className="px-4 py-2 text-gray-400 hover:text-gray-100"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
			<input
				ref={importInputRef}
				type="file"
				accept=".json,application/json"
				className="hidden"
				onChange={handleImportChange}
				aria-label="Import tier list JSON file"
			/>
		</div>
	);
};

const App = () => {
	const [isHelpOpen, setIsHelpOpen] = useState(false);

	const normalizeStyle = (value: unknown): Style => {
		const fallback: Style = {
			ratio: "preserve",
			size: 120,
			quality: 100,
			pasteScaleMode: "preserve",
		};

		if (typeof value !== "object" || value === null) {
			return fallback;
		}

		const styleValue = value as Partial<Style>;

		return {
			ratio: typeof styleValue.ratio === "string" ? styleValue.ratio : fallback.ratio,
			size: typeof styleValue.size === "number" ? styleValue.size : fallback.size,
			quality: typeof styleValue.quality === "number" ? styleValue.quality : fallback.quality,
			pasteScaleMode:
				styleValue.pasteScaleMode === "preserve" || styleValue.pasteScaleMode === "fixed"
					? styleValue.pasteScaleMode
					: fallback.pasteScaleMode,
		};
	};

	const [style, setStyle] = useState<Style>(() => {
		const storedStyle = localStorage.getItem("style");
		if (storedStyle) {
			if (storedStyle.startsWith("{")) {
				return normalizeStyle(JSON.parse(storedStyle));
			} else {
				// MIGRATION: OLD STYLE FORMAT
				const ratio = storedStyle;
				return { ratio: ratio, size: 120, quality: 100, pasteScaleMode: "preserve" };
			}
		} else {
			return { ratio: "preserve", size: 120, quality: 100, pasteScaleMode: "preserve" };
		}
	});
	const [tiers, setTiers] = useState<Tier[]>(() => {
		const storedTiers = localStorage.getItem("tiers");

		if (storedTiers) {
			const parsedTiers: any[] = JSON.parse(storedTiers);

			// MIGRATION: ID USED TO REPRESENT TIER LABEL
			// IDENTIFIER USED COMBINATION OF COLOR AND LABEL
			// WE WILL MIMIC THE OLD BEHAVIOR FOR OLDER SAVES (OLD SAVES USE THIS FOR IMAGE LOOKUP)
			// FUTURE SAVES WILL USE NEW FORMAT OF DATE.NOW()
			// this is my first time writing migration code and writing comments about it :3
			// if you see this DM me I'm convinced nobody will ever read this
			const isOldFormat = parsedTiers.some((tier) => !("tierLabel" in tier));
			if (isOldFormat) {
				return parsedTiers.map((tier, index) => ({
					color: tier.color,
					tierLabel: tier.id,
					id: `${tier.color}_${tier.id}`,
				}));
			} else {
				return parsedTiers;
			}
		}

		return [
			{ color: "#FF7F7F", tierLabel: "S", id: 1 },
			{ color: "#FFBF7F", tierLabel: "A", id: 2 },
			{ color: "#FFDF80", tierLabel: "B", id: 3 },
			{ color: "#FFFF7F", tierLabel: "C", id: 4 },
			{ color: "#BFFF7F", tierLabel: "D", id: 5 },
		];
	});

	useEffect(() => {
		localStorage.setItem("tiers", JSON.stringify(tiers));
	}, [tiers]);

	useEffect(() => {
		localStorage.setItem("style", JSON.stringify(style));
	}, [style]);

	useEffect(() => {
		// Also migrates image stores from localStorage to IndexedDB
		migrateImageStoresFromLocalStorage();
	}, []);

	return (
		<div className="min-h-[100vh] bg-stone-800 pt-4">
			<StylingContext.Provider value={{ style, setStyle }}>
				<TierContext.Provider value={{ tiers, setTiers }}>
					<>
						<div className="px-8 w-full flex justify-end justify-items-end items-end"><h5 className="text-sm font-semibold text-gray-300 opacity-40">tiersofjoy.pages.dev</h5></div>
						<div className="mt-5 px-8 pb-8">
							<TierList />
							<div className="flex flex-col sm:flex-row gap-2 mt-4 items-start sm:items-center">
								<AddTierButton />
								<button
									onClick={() => setIsHelpOpen(true)}
									className="flex items-center gap-1.5 bg-stone-600 hover:bg-stone-500 text-white text-sm font-medium py-2 px-3 rounded"
									title="How To Edit"
								>
									<QuestionIcon />
									How To Edit
								</button>
								<GlobalSettingsMenu />
							</div>
						</div>
						<ImageHolder />
						{isHelpOpen && <HowToEditModal onClose={() => setIsHelpOpen(false)} />}
						<footer className="px-8 py-4 flex justify-center">
							<a
								href="https://codeberg.org/unique2/tiersofjoy"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
							>
								Proudly Open Source
								<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
									<polyline points="15 3 21 3 21 9" />
									<line x1="10" y1="14" x2="21" y2="3" />
								</svg>
							</a>
						</footer>
					</>
				</TierContext.Provider>
			</StylingContext.Provider>
		</div>
	);
};

export default App;
